import { useState } from 'react';
import { getFormUrl } from '@/mocks/newsletter-form';

export default function NewsletterCTA() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    if (!email.trim()) {
      setStatus('error');
      setErrorMsg('请输入邮箱地址');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: email.trim(), source: 'homepage' }).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text response
      }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText
          || '订阅失败，请稍后重试';
        setStatus('error');
        setErrorMsg(String(msg));
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请检查连接后重试');
    }
  };

  return (
    <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-primary-500">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-semibold text-background-50">
          获取色彩设计灵感
        </h2>
        <p className="mt-3 text-sm md:text-base text-primary-100 leading-relaxed">
          订阅我们的邮件通讯，每周获取精选配色方案、设计案例和行业洞察
        </p>

        {status === 'success' ? (
          <div className="mt-8 flex items-center justify-center gap-2 text-background-50">
            <i className="ri-checkbox-circle-line text-xl"></i>
            <span className="text-sm font-medium">订阅成功！感谢你的关注</span>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            action={getFormUrl()}
            method="POST"
            className="mt-8 flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
            data-readdy-form="newsletter-homepage"
            id="newsletter-homepage"
          >
            <div className="honeypot-field" aria-hidden="true">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                aria-hidden="true"
              />
            </div>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入你的邮箱地址"
              required
              className="w-full sm:flex-1 px-4 py-3 bg-background-50 text-foreground-900 rounded-full text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-background-50/50"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto px-6 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {status === 'loading' ? (
                <span className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  订阅中
                </span>
              ) : (
                '立即订阅'
              )}
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="mt-3 text-sm text-primary-100/90">{errorMsg}</p>
        )}

        <p className="mt-4 text-xs text-primary-200">
          我们尊重你的隐私，不会发送垃圾邮件，可随时退订
        </p>
      </div>
    </section>
  );
}